// frontend/src/components/HairFlow3D.js
//
// The hero background: a thousand hair strands, grouped into locks, falling in
// 3D past the camera, which orbits slightly toward the pointer. Written
// against raw WebGL rather than three.js on purpose — three + a renderer is
// ~600KB gzipped and this ships inside the Capacitor Android bundle, where the
// whole app is currently smaller than that. What we need here is one draw call
// of gold lines, which is a shader, not a scene graph.
//
// Every strand is a LINE strip expanded into segment pairs so the whole field
// is a single gl.LINES draw. The vertex shader does the projection by hand
// (perspective divide included) so there is no matrix library either.
import React, { useEffect, useRef, useState } from 'react';
import theme from '../theme';

// A phone shows the fall at roughly a third of the width, so a third of the
// strands land on screen anyway — drawing the full thousand there costs
// battery for detail nobody can resolve.
const strandCount = () => (typeof window !== 'undefined' && window.innerWidth < 760 ? 480 : 1100);
const SEGMENTS = 34;          // points per strand; segments drawn = SEGMENTS - 1
const GOLD = [0.788, 0.663, 0.431];   // #C9A96E, the brand gold

const VERT = `
precision mediump float;

attribute float a_s;   // strand index, normalised 0..1
attribute float a_t;   // position along the strand, 0 at the crown, 1 at the tip

uniform float u_time;
uniform vec2  u_look;    // camera orbit: pointer, or drift on touch devices
uniform float u_scroll;  // 0 at the top of the hero, 1 once it is scrolled away
uniform float u_aspect;
uniform float u_strands;
uniform float u_widen;   // >1 spreads the fall across wide, short banners

varying float v_a;
varying float v_lit;
varying float v_shine;

float hash(float n) { return fract(sin(n * 12.9898) * 43758.5453); }

const float LOCKS = 26.0;

void main() {
  float sid    = a_s * u_strands;
  // Hair falls in locks, not as evenly scattered filaments. Strands inside a
  // lock run nearly parallel, wave in phase and sway together; spread them
  // evenly instead and the whole thing reads as thread on a spool.
  float lockId = floor(a_s * LOCKS);
  float within = fract(a_s * LOCKS);
  float hl1 = hash(lockId * 3.17 + 1.7);
  float hl2 = hash(lockId * 7.71 + 5.3);
  float hs1 = hash(sid + 11.31);
  float hs2 = hash(sid + 27.77);

  // The crown is a small disc sitting above the top of the frame, not a point
  // inside it: strands converging on a visible apex read as a spotlight, and
  // the whole effect collapses into light rays. Starting them apart, off
  // screen, is what makes the field read as hair falling past the camera.
  float lockAng = (lockId / LOCKS) * 6.2831853 + hl1 * 0.30 + u_time * 0.025;
  float ang     = lockAng + (within - 0.5) * (0.17 + hl2 * 0.11) + (hs1 - 0.5) * 0.025;

  // Two periods per lock: a long curl that pushes the lock in and out, and a
  // shorter wave across it. Together they give the loose-wave texture the
  // catalogue actually sells.
  float phase = hl1 * 6.283;
  float curl  = sin(a_t * 2.6 + phase * 1.7) * 0.34 * a_t;
  float wave  = sin(a_t * 5.2 + phase) * 0.26 * a_t;

  float spread = 1.00 + hl2 * 2.15 + hs1 * 0.14;
  float r      = mix(0.22, spread, pow(a_t, 1.12)) + curl;
  float sway   = sin(u_time * 0.50 + phase + a_t * 2.0) * 0.15 * a_t * a_t;

  // Length varies per strand so the ends feather instead of stopping on a line.
  float len = 4.35 + hl2 * 0.60 + hs2 * 0.55;

  vec3 p = vec3(
    cos(ang) * r - sin(ang) * wave + sway,
    2.60 - a_t * len,
    sin(ang) * r + cos(ang) * wave
  );

  // A few degrees of roll: a symmetric cone reads as a lampshade, the same
  // fall tipped over reads as hair.
  float ct = cos(0.20), st = sin(0.20);
  p = vec3(p.x * ct - p.y * st, p.x * st + p.y * ct, p.z);

  // Orbit the camera instead of the object so the lighting-by-depth below
  // stays consistent. Scrolling lifts the whole fall out of frame.
  float yaw = u_look.x * 0.55;
  float pit = u_look.y * 0.30 - u_scroll * 0.25;

  float c = cos(yaw), s = sin(yaw);
  p = vec3(p.x * c - p.z * s, p.y, p.x * s + p.z * c);
  c = cos(pit); s = sin(pit);
  p = vec3(p.x, p.y * c - p.z * s, p.y * s + p.z * c);

  p.y += u_scroll * 0.9;
  p.z -= 3.95;

  vec2 proj = vec2(p.x, p.y) * 2.25 / -p.z;
  // A page-header band is far wider than it is tall, and dividing by that
  // aspect alone squeezes the fall into a thin ribbon down the middle.
  proj.x *= u_widen / u_aspect;
  gl_Position = vec4(proj, 0.0, 1.0);

  // Depth does the work of a light: strands at the front of the fall are lit,
  // the ones behind sink into brown and then into the black.
  float depth = clamp((-p.z - 2.0) / 3.7, 0.0, 1.0);
  float near  = 1.0 - depth;

  // pow() lifts the middle of the range: with a linear falloff most of the
  // fall sat half-way between brown and gold, i.e. mud.
  v_lit = pow(near, 0.65);

  // The specular halo — the horizontal band of shine that sits a little above
  // the middle of a fall of hair. It is the single cue that separates hair
  // from any other bundle of curved lines, so it drives opacity as well as
  // colour: the band has to read as denser, not just paler.
  float band = exp(-pow((a_t - 0.44) / 0.20, 2.0));
  v_shine = band * near;

  // Both ends fade so nothing terminates on a hard dot.
  v_a = near
      * smoothstep(0.0, 0.08, a_t)
      * (1.0 - smoothstep(0.78, 1.0, a_t))
      * (0.30 + 0.70 * hs1)
      * (0.60 + 0.85 * band);
}
`;

const FRAG = `
precision mediump float;
uniform vec3 u_gold;
uniform float u_fade;   // entrance fade-in
varying float v_a;
varying float v_lit;
varying float v_shine;

void main() {
  // Deep brown in the mass, gold where the light reaches, near-white along the
  // shine band. Additive blending then builds the lit strands into a sheen
  // while the shadowed ones only add body.
  vec3 col = mix(vec3(0.24, 0.15, 0.08), u_gold, v_lit);
  col = mix(col, vec3(1.0, 0.96, 0.88), v_shine * 0.80);
  gl_FragColor = vec4(col, v_a * 0.58 * u_fade);
}
`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

export default function HairFlow3D({ children, widen = 1, wash, vignette }) {
  const canvasRef = useRef(null);
  // Anything that stops the canvas from drawing — no WebGL, a lost context, a
  // shader that would not compile — falls back to the CSS orbs passed as
  // children, so the hero is never an empty black rectangle.
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const gl = canvas.getContext('webgl', { alpha: true, antialias: true, premultipliedAlpha: false })
            || canvas.getContext('experimental-webgl', { alpha: true, antialias: true });
    if (!gl) { setFailed(true); return undefined; }

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) { setFailed(true); return undefined; }

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { setFailed(true); return undefined; }
    gl.useProgram(prog);

    // One interleaved buffer of segment endpoints: [s, t] per vertex, two
    // vertices per segment. 1,100 strands x 33 segments = 36,300 lines, one call.
    const strands = strandCount();
    const verts = new Float32Array(strands * (SEGMENTS - 1) * 2 * 2);
    let k = 0;
    for (let s = 0; s < strands; s++) {
      const sn = s / strands;
      for (let i = 0; i < SEGMENTS - 1; i++) {
        verts[k++] = sn; verts[k++] = i / (SEGMENTS - 1);
        verts[k++] = sn; verts[k++] = (i + 1) / (SEGMENTS - 1);
      }
    }
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);

    const aS = gl.getAttribLocation(prog, 'a_s');
    const aT = gl.getAttribLocation(prog, 'a_t');
    gl.enableVertexAttribArray(aS);
    gl.vertexAttribPointer(aS, 1, gl.FLOAT, false, 8, 0);
    gl.enableVertexAttribArray(aT);
    gl.vertexAttribPointer(aT, 1, gl.FLOAT, false, 8, 4);

    const uTime    = gl.getUniformLocation(prog, 'u_time');
    const uLook    = gl.getUniformLocation(prog, 'u_look');
    const uScroll  = gl.getUniformLocation(prog, 'u_scroll');
    const uAspect  = gl.getUniformLocation(prog, 'u_aspect');
    const uStrands = gl.getUniformLocation(prog, 'u_strands');
    const uWiden   = gl.getUniformLocation(prog, 'u_widen');
    const uGold    = gl.getUniformLocation(prog, 'u_gold');
    const uFade    = gl.getUniformLocation(prog, 'u_fade');

    gl.uniform1f(uStrands, strands);
    gl.uniform3f(uGold, GOLD[0], GOLD[1], GOLD[2]);
    gl.uniform1f(uWiden, widen);

    // Additive: overlapping strands build into a glow instead of flattening.
    gl.disable(gl.DEPTH_TEST);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE);

    let width = 0, height = 0;
    const resize = () => {
      // DPR above 2 buys nothing visible on hairlines and costs 2.25x the
      // fragments on a phone.
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = Math.round(canvas.clientWidth * dpr);
      const h = Math.round(canvas.clientHeight * dpr);
      if (w === width && h === height) return;
      width = w; height = h;
      canvas.width = w; canvas.height = h;
      gl.viewport(0, 0, w, h);
      gl.uniform1f(uAspect, h ? w / h : 1);
    };
    resize();

    const reduced = window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

    const look = { x: 0, y: 0, tx: 0, ty: 0 };
    const onPointer = (e) => {
      look.tx = (e.clientX / window.innerWidth) * 2 - 1;
      look.ty = (e.clientY / window.innerHeight) * 2 - 1;
    };
    // Touch devices get a slow drift instead of a pointer — nothing here should
    // depend on a hover the device cannot produce.
    const hasPointer = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    if (hasPointer && !reduced) window.addEventListener('pointermove', onPointer, { passive: true });

    let scroll = 0;
    const onScroll = () => {
      scroll = Math.min(1, (window.scrollY || 0) / Math.max(1, window.innerHeight));
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });

    let raf = 0;
    let visible = true;
    const start = performance.now();

    const draw = (now) => {
      resize();
      const t = (now - start) / 1000;

      if (hasPointer && !reduced) {
        look.x += (look.tx - look.x) * 0.045;
        look.y += (look.ty - look.y) * 0.045;
      } else if (!reduced) {
        look.x = Math.sin(t * 0.11) * 0.55;
        look.y = Math.cos(t * 0.08) * 0.22;
      }

      gl.uniform1f(uTime, reduced ? 6.0 : t);
      gl.uniform2f(uLook, look.x, look.y);
      gl.uniform1f(uScroll, scroll);
      gl.uniform1f(uFade, Math.min(1, t / 1.6));

      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.LINES, 0, strands * (SEGMENTS - 1) * 2);

      if (!reduced && visible) raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    // Off-screen or backgrounded, the loop stops: a hero animation has no
    // business burning a phone battery three sections down the page.
    const io = new IntersectionObserver(([entry]) => {
      const nowVisible = entry.isIntersecting;
      if (nowVisible && !visible && !reduced) {
        visible = true;
        raf = requestAnimationFrame(draw);
      } else {
        visible = nowVisible;
      }
    }, { threshold: 0 });
    io.observe(canvas);

    const onVisibility = () => {
      if (document.hidden) {
        cancelAnimationFrame(raf);
      } else if (visible && !reduced) {
        raf = requestAnimationFrame(draw);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    const onLost = (e) => { e.preventDefault(); setFailed(true); };
    canvas.addEventListener('webglcontextlost', onLost);

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener('pointermove', onPointer);
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onVisibility);
      canvas.removeEventListener('webglcontextlost', onLost);
      gl.deleteBuffer(buf);
      gl.deleteProgram(prog);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
    };
  }, [widen]);

  return (
    <>
      {/* The warm core behind the strands. CSS, not a shader — it is a flat
          radial wash and does not need to cost a fragment pass. */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: wash || `radial-gradient(ellipse 75% 60% at 50% 38%, ${theme.alpha.gold(0.15)} 0%, transparent 72%)`,
      }} />
      {!failed && (
        <canvas
          ref={canvasRef}
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', display: 'block' }}
        />
      )}
      {failed && children}
      {/* Vignette: keeps the strands from running into the headline. */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: vignette || `radial-gradient(ellipse 40% 30% at 50% 47%, ${theme.alpha.inkDeep(0.66)} 0%, ${theme.alpha.inkDeep(0.26)} 46%, transparent 76%)`,
      }} />
    </>
  );
}
